import AuthBase from "../AuthBase";
import {useContext, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router";
import {
    Avatar, IconButton,
    Stack,
} from "@mui/material";
import Masonry from '@mui/lab/Masonry';
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import ConfigContext from "../../context/ConfigContext";
import Loading from "../../components/Loading";
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import ExtraItemTemplate from "./ExtraItemTemplate";

import {truncateText} from "../../utils/FormUtils";
import General from "./components/General";
import Visibility from "./components/Visibility";
import Image from "./components/Image";
import {mergeDataIfNotNull} from "../../utils/misc";
import {useSnackbar} from "notistack";
import Invoice from "./components/Invoice";
import useServiceQueries from "../../hooks/queries/useServiceQueries";
import useExtraItemQueries from "../../hooks/queries/useExtraItemQueries";
import {useEffect} from "react";


const ExtraItemDetail = () => {
    const navigate = useNavigate()
    const {baseUrl} = useContext(ConfigContext)
    const {enqueueSnackbar} = useSnackbar()

    const params = useParams()

    const generalRef = useRef(null)
    const visibilityRef = useRef(null)
    const imageRef = useRef(null)
    const invoiceRef = useRef(null)


    const {serviceBusinessQuery} = useServiceQueries()
    const {useGetExtraItemById, updateExtraItemMutation, addServiceMutation, removeServiceMutation} = useExtraItemQueries()
    const {data: services, isPending: servicesLoading} = serviceBusinessQuery
    const {data: extraItem, isPending: extraItemLoading} = useGetExtraItemById(params.extraItemId)
    const [editedExtraItem, setEditedExtraItem] = useState(extraItem||ExtraItemTemplate)

    const addService = service => addServiceMutation.mutate({id: extraItem.id, service: service.id})
    const removeService = service => removeServiceMutation.mutate({id: extraItem.id, service: service.id})

    const hasUnsavedChanges = JSON.stringify(extraItem) !== JSON.stringify(editedExtraItem)

    useEffect(() => {
        if (extraItem) {
            setEditedExtraItem(extraItem);
        }
    }, [extraItem]);

    const savePageData = () => {
        const generalRefData = generalRef.current.getData();
        const visibilityRefData = visibilityRef.current.getData();
        const imageRefData = imageRef.current.getData();
        const invoiceRefData = invoiceRef.current.getData();
        const refData = [
            generalRefData,
            visibilityRefData,
            imageRefData,
            invoiceRefData
        ]
        const invalidRefs = refData.filter(ref => ref === null)
        if (invalidRefs.length === 0) {
            const allData = mergeDataIfNotNull(refData);
            const isFile = allData?.image instanceof File;
            if (!isFile) delete allData['image']

            updateExtraItemMutation.mutate({id: extraItem?.id, data: allData}, {
                onSuccess: response => {
                    setEditedExtraItem(response)
                    enqueueSnackbar('Mentve!', {variant: 'success'})
                }
            })
        } else {
            enqueueSnackbar('Kérlek javísd a hibákat!', {variant: 'error'})
        }
    }

    const buttons = [{
        name: 'Változtatások mentése',
        props: {
            variant: 'contained',
            disabled: !hasUnsavedChanges,
            endIcon: <SaveRoundedIcon/>,
            onClick: savePageData
        }
    }]

    const buttonsMobile = [{
        name: 'Mentés',
        props: {
            variant: 'contained',
            size: 'small',
            disabled: !hasUnsavedChanges,
            endIcon: <SaveRoundedIcon/>,
            onClick: savePageData
        }
    }]

    if (servicesLoading||extraItemLoading) return <Loading isLoading />

    return <AuthBase label={extraItem?.name} buttons={buttons} filterButtonsMobile={buttonsMobile}>
        <div className="main">
            <Button variant='text' startIcon={<ArrowBackIosNewRoundedIcon/>}
                    onClick={() => navigate('/extra-items')}>vissza</Button>
            <Masonry columns={{xs: 1, sm: 1, md: 2, xl: 2}} spacing={2} sx={{margin: 0}}>
                {/*General data*/}
                <div className="white-card">
                    <Typography variant='subtitle1'>Általános adatok</Typography>
                    <Typography variant='body2' color='#536B74'>Add meg a tétel nevét és leírását!</Typography>
                    <General reference={generalRef} data={editedExtraItem} setData={setEditedExtraItem}/>
                </div>

                {/*/!*Visibility*!/*/}
                <div className="white-card">
                    <Typography variant='subtitle1'>Láthatóság</Typography>
                    <Typography variant='body2' color='#536B74'>Ha nem szeretnéd, hogy a tétel látható legyen
                        a kliensek számára akkor kikapcsolhatod. (Az elrejtés előtt leadott foglalások nem fognak
                        törlődni!)</Typography>
                    <Visibility reference={visibilityRef} data={editedExtraItem} setData={setEditedExtraItem}/>
                </div>

                {/*/!*Picture*!/*/}
                <div className="white-card">
                    <Typography variant='subtitle1'>Tétel képe</Typography>
                    <Typography variant='body2' color='#536B74'>
                        Tölts fel egy a tételre jellemző képet!
                    </Typography>
                    <Image reference={imageRef} data={editedExtraItem} setData={setEditedExtraItem}/>
                </div>

                {/*/!*Invoice*!/*/}
                <div className="white-card">
                    <Typography variant='subtitle1'>Számlázási beállítások</Typography>
                    <Typography variant='body2' color='#536B74'>
                        Írd be a számlázásnál hogyan kezeljük a terméket!
                    </Typography>
                    <Invoice reference={invoiceRef} data={editedExtraItem} setData={setEditedExtraItem}/>
                </div>

                {/*Services*/}
                <div className="white-card">
                    <Typography variant='subtitle1'>Szolgáltatások</Typography>
                    <Typography variant='body2' color='#536B74'>
                        Vedd fel azokat a szolgáltatásokat ahol szeretnéd, hogy elérhető legyen a tétel!
                    </Typography>
                    <Stack spacing={2} marginTop={2}>
                        {services.map((s, i) => <Stack direction='row' key={`price_${i}`}
                                                       justifyContent="space-between">
                            <Stack direction='row' spacing={2} alignItems='center'>
                                <Avatar src={baseUrl + s?.image}
                                        sx={{width: '48px', height: '48px'}}/>
                                <Stack>
                                    <Typography variant='subtitle2'>{s.name}</Typography>
                                    <Typography variant='caption' color='#536B74'>
                                        {truncateText(s.description, 150)}
                                    </Typography>
                                </Stack>
                            {/*</Stack>*/}
                            {/*<Stack direction='row' spacing={1} alignItems='center'>*/}
                                {editedExtraItem?.services?.includes(s?.id) ?
                                    <IconButton
                                        sx={{
                                            border: '2px solid',
                                            borderColor: 'primary.main',
                                            background: '#fff',
                                            color: 'primary.main'
                                        }}
                                        variant='outlined'
                                        onClick={() => removeService(s)}>
                                        <RemoveRoundedIcon/>
                                    </IconButton>
                                    :
                                    <IconButton
                                        sx={{
                                            border: '2px solid',
                                            borderColor: 'primary.main',
                                            background: '#fff',
                                            color: 'primary.main'
                                        }}
                                        variant='contained'
                                        onClick={() => addService(s)}>
                                        <AddRoundedIcon/>
                                    </IconButton>
                                }
                            </Stack>
                        </Stack>)}
                    </Stack>

                </div>

            </Masonry>

        </div>

    </AuthBase>

}

export default ExtraItemDetail